'use client'

import { X24 } from '@vinted/monochrome-icons'
import {
  Button,
  Cell,
  Dialog,
  Divider,
  InfoBanner,
  InputSelect,
  InputTextArea,
  Navigation,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { MouseEvent, useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { useDropOffPointMapContext } from '@marketplace-web/domain/core-conversation'
import { useAbTest, useTrackAbTest } from '@marketplace-web/shared/ab-tests'
import { useFetch } from '@marketplace-web/shared/api-client'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ContentLoader } from '@marketplace-web/shared/ui-helpers'
import { renderValidation, useFormValidationMessage } from '@marketplace-web/shared/react-hook-form'
import { ErrorState } from '@marketplace-web/shared/error-display'
import { UiState } from '@marketplace-web/shared/ui-state'

import { clickEvent, selfServiceViewScreenEvent } from '_libs/common/event-tracker/events'
import { DistanceUnitEnum } from 'constants/distance-unit'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { cancelTransaction } from 'data/api/cancel-transaction'
import { getDefaultShippingAddress } from 'data/api/get-default-shipping-address'
import { getTransactionCancellationReasons } from 'data/api/get-transaction-cancellation-reasons'
import { transformDefaultShippingAddress } from 'data/api/transformers/response/transform-default-shipping-address'
import { transformTransactionCancellationReasonsDto } from 'data/transformers/transaction-cancellation'
import useLoadGoogleApi from 'hooks/google/useLoadGoogleApi'
import { TransactionCancellationReasonModel } from 'types/models/transaction-cancellation'

import { Field, FormModel } from './types'
import useRequiredFormField from './useRequiredFormField'

const USER_EXPLANATION_MAX_LENGTH = 100

type Props = {
  show: boolean
  transactionId: number
  isBuyer: boolean
  onSuccess: () => void
  onCancel: () => void
  onDropOffPointModalOpen: () => void
  onCarrierChangeModalOpen: () => void
}

const CancelTransactionModal = ({
  show,
  transactionId,
  onSuccess,
  onCancel,
  onDropOffPointModalOpen,
  onCarrierChangeModalOpen,
  isBuyer,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('conversation.conversation_info.cancel_transaction_modal')
  const { fetchNearbyDropOffPoints, selectedDropOffPoint } = useDropOffPointMapContext()
  const carrierChangePostOrderAbTest = useAbTest('carrier_change_post_order_v2')

  const [uiState, setUiState] = useState<UiState>(UiState.Idle)
  const [reasons, setReasons] = useState<Array<TransactionCancellationReasonModel>>([])

  const { fetch: fetchDefaultShippingAddress, transformedData: defaultShippingAddress } = useFetch(
    getDefaultShippingAddress,
    transformDefaultShippingAddress,
  )

  const formMethods = useForm<FormModel>({
    defaultValues: { [Field.Reason]: '' },
  })

  const {
    control,
    watch,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = formMethods

  const validationMessage = useFormValidationMessage(errors)
  const requiredField = useRequiredFormField()

  const selectedReasonId = watch<Field.Reason>(Field.Reason)

  const isGoogleApiLoaded = useLoadGoogleApi()

  useTrackAbTest(carrierChangePostOrderAbTest, !isBuyer && show)

  useEffect(() => {
    if (!show) return

    track(
      selfServiceViewScreenEvent({
        screen: Screen.TransactionCancellationReasons,
        transactionId,
      }),
    )
  }, [show, track, transactionId])

  useEffect(() => {
    async function fetchTransactionCancellationReasons() {
      setUiState(UiState.Pending)

      const response = await getTransactionCancellationReasons(transactionId)

      if ('errors' in response) {
        setUiState(UiState.Failure)

        return
      }

      const transformedReasons = transformTransactionCancellationReasonsDto(response.reasons)

      setReasons(transformedReasons)
      setUiState(UiState.Success)
    }

    if (!show) return

    fetchTransactionCancellationReasons()
  }, [show, transactionId])

  useEffect(() => {
    if (defaultShippingAddress) return

    if (show) fetchDefaultShippingAddress()
  }, [defaultShippingAddress, fetchDefaultShippingAddress, show])

  useEffect(() => {
    if (!isGoogleApiLoaded || !defaultShippingAddress || isBuyer) return

    if (show) {
      fetchNearbyDropOffPoints({ address: defaultShippingAddress })
    }
  }, [fetchNearbyDropOffPoints, defaultShippingAddress, show, isGoogleApiLoaded, isBuyer])

  const handleFormSubmit = handleSubmit(async values => {
    const { reason, userExplanation } = values

    const response = await cancelTransaction({
      id: transactionId,
      reasonId: Number(reason),
      userExplanation,
    })

    if ('errors' in response) {
      setUiState(UiState.Failure)

      return
    }

    onSuccess()
  })

  function renderReason() {
    const options = reasons.map(reason => ({ value: reason.id, label: reason.reason }))

    return (
      <Controller
        name={Field.Reason}
        render={({ field }) => (
          <InputSelect
            {...field}
            options={options}
            title={translate('fields.reason.title')}
            placeholder={translate('fields.reason.placeholder')}
            required
            validation={renderValidation(validationMessage(Field.Reason))}
          />
        )}
        control={control}
        rules={{
          required: requiredField(Field.Reason),
        }}
      />
    )
  }

  function getDistanceToNearestDropOffPoint() {
    if (!selectedDropOffPoint) return null

    const { distance, distanceUnit } = selectedDropOffPoint

    if (!distanceUnit) return null
    if (!distance && distance !== 0) return null

    if (distanceUnit === DistanceUnitEnum.Kilometers || distanceUnit === DistanceUnitEnum.Miles) {
      const roundedDistance = distance.toFixed(1)

      return `${roundedDistance} ${distanceUnit}`
    }

    const roundedDistance = Math.round(distance)

    return `${roundedDistance} ${distanceUnit}`
  }

  function trackDropOffPointModalOpenClick() {
    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: ClickableElement.LinkDropoffMap,
        screen: Screen.TransactionCancellationReasons,
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  function handleDropOffPointModalOpen() {
    trackDropOffPointModalOpenClick()

    onDropOffPointModalOpen()
  }

  function handleCarrierChangeSuggestionClick(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault()
    onCarrierChangeModalOpen()
  }

  function renderNearestDropOffPoint() {
    if (!selectedReasonId) return null

    const reason = reasons.find(({ id }) => id === Number(selectedReasonId))

    if (!reason?.showNearestDropOffPoint) return null
    if (!selectedDropOffPoint) return null

    return (
      <InfoBanner
        body={
          <Text
            as="span"
            text={translate('nearest_drop_off_point', {
              distance: getDistanceToNearestDropOffPoint(),
              'see-drop-off-points': chunks => (
                <button type="button" onClick={handleDropOffPointModalOpen}>
                  <Text clickable underline text={chunks} as="span" />
                </button>
              ),
            })}
            html
          />
        }
        type={InfoBanner.Type.Info}
        testId="cancel-transaction-modal-nearest-drop-off-point"
      />
    )
  }

  function renderCarrierChangeSuggestionBanner() {
    if (!selectedReasonId) return null

    const reason = reasons.find(({ id }) => id === Number(selectedReasonId))

    if (!reason?.bannerText) return null

    return (
      <InfoBanner
        body={
          <span role="none" onClick={handleCarrierChangeSuggestionClick}>
            <Text
              text={reason.bannerText}
              html
              testId="carrier-change-suggestion-info-banner-text"
              as="span"
            />
          </span>
        }
        type={InfoBanner.Type.Info}
      />
    )
  }

  function renderUserExplanation() {
    if (!selectedReasonId) return null

    const reason = reasons.find(({ id }) => id === Number(selectedReasonId))

    if (!reason?.hasInput) return null

    return (
      <Controller
        name={Field.UserExplanation}
        render={({ field }) => (
          <InputTextArea
            {...field}
            rows={4}
            maxLength={USER_EXPLANATION_MAX_LENGTH}
            validation={renderValidation(validationMessage(Field.UserExplanation))}
            title={translate('fields.user_explanation.title')}
          />
        )}
        defaultValue={null}
        rules={{
          required: requiredField(Field.UserExplanation),
        }}
      />
    )
  }

  function renderContent() {
    return (
      <FormProvider {...formMethods}>
        <Cell body={translate('body')} />
        {renderReason()}
        {renderNearestDropOffPoint()}
        {renderCarrierChangeSuggestionBanner()}
        {renderUserExplanation()}
      </FormProvider>
    )
  }

  function renderLoader() {
    return <ContentLoader styling={ContentLoader.Styling.Wide} />
  }

  function renderActions() {
    return (
      <Cell>
        <Button
          disabled={uiState === UiState.Pending || isSubmitting}
          text={translate('buttons.cancel_this_order')}
          styling={Button.Styling.Filled}
          theme="warning"
          onClick={handleFormSubmit}
          testId="cancel-transaction-modal-cancel-this-order"
        />
        <Spacer size={Spacer.Size.Large} />
        <Button
          text={translate('buttons.no_go_back')}
          styling={Button.Styling.Flat}
          theme="warning"
          onClick={onCancel}
          testId="cancel-transaction-modal-no-go-back"
        />
      </Cell>
    )
  }

  function renderModalContent() {
    return (
      <>
        {uiState === UiState.Pending ? renderLoader() : renderContent()}
        {renderActions()}
      </>
    )
  }

  function renderError() {
    return <ErrorState />
  }

  return (
    <Dialog show={show} testId="cancel-transaction-modal">
      <Navigation
        body={translate('title')}
        right={
          <Button
            styling={Button.Styling.Flat}
            onClick={onCancel}
            iconName={X24}
            inline
            testId="cancel-transaction-modal-close"
          />
        }
      />
      <Divider />
      {uiState === UiState.Failure ? renderError() : renderModalContent()}
    </Dialog>
  )
}
export default CancelTransactionModal
